// Pure task-line tokenizer matching docs/markdown-syntax.md (default + todotxt dialects).
// Used for editor decorations, autocomplete context and quick-add previews.
// No DOM / CodeMirror imports here: keep it unit-testable.

export type TokenKind =
  | "completion" // YYYY-MM-DD directly after [x]
  | "priority" // (A)..(Z)
  | "due"
  | "start"
  | "project" // +Name
  | "person" // @handle
  | "label" // #label
  | "status"
  | "recurrence"
  | "bucket" // bucket:Name (spaces as "-")
  | "field" // key:value
  | "link" // [[TARGET]]
  | "anchor" // ^anchor (last token)
  | "code" // `inline code` (kept in title)
  | "word"; // plain title word

export interface Token {
  kind: TokenKind;
  /** Offsets relative to the start of the line. */
  from: number;
  to: number;
  text: string;
  /** Token payload: date string, handle without "@", label without "#", field value, ... */
  value: string;
  /** For `field`: the key. For `start`: "t" or "start". */
  key?: string;
}

export interface TaskLine {
  indent: string;
  marker: string; // "-", "*", "+", "1." ...
  checked: boolean;
  /** Offsets of the "[ ]" / "[x]" box (relative to line start). */
  boxFrom: number;
  boxTo: number;
  /** Offset where the content (after the checkbox) starts. */
  contentFrom: number;
  tokens: Token[];
  title: string;
  priority: number | null; // 0..4 mapped value (A=4, B=3, C=2, D..Z=1)
  priorityLetter: string | null;
  due: string | null; // raw value
  start: string | null; // raw value
  completion: string | null;
  projects: string[];
  people: string[];
  labels: string[];
  status: string | null;
  recurrence: string | null;
  /** v0.4: raw `bucket:` value (dashes stand for spaces). */
  bucket: string | null;
  fields: Record<string, string>;
  links: string[];
  anchor: string | null;
}

export type Dialect = "default" | "todotxt";

const TASK_RE = /^(\s*)([-*+]|\d+[.)])(\s+)\[([ xX])\]/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const PRIORITY_RE = /^\(([A-Z])\)$/;
const PROJECT_RE = /^\+[\p{L}\p{N}_][\p{L}\p{N}:\-_.]*$/u;
const PERSON_RE = /^@[\p{L}\p{N}_][\p{L}\p{N}_.\-]*(?:@[\p{L}\p{N}][\p{L}\p{N}.\-]*)?$/u;
const LABEL_RE = /^#[\p{L}\p{N}\-_/]+$/u;
const ANCHOR_RE = /^\^[a-z0-9]{4,12}$/;
const FIELD_RE = /^([a-z][a-z0-9_-]*):(\S+)$/;
const LINK_RE = /^\[\[([^\]]+)\]\]$/;
const URL_SCHEMES = new Set(["http", "https", "mailto", "ftp", "file"]);

export function priorityValue(letter: string): number {
  if (letter === "A") return 4;
  if (letter === "B") return 3;
  if (letter === "C") return 2;
  return 1;
}

export function priorityLetter(value: number): string | null {
  switch (value) {
    case 4:
      return "A";
    case 3:
      return "B";
    case 2:
      return "C";
    case 1:
      return "D";
    default:
      return null;
  }
}

interface Segment {
  from: number;
  to: number;
  text: string;
  atomic: "code" | "link" | null;
}

/** Split content into whitespace-separated segments; code spans and [[links]] are atomic. */
function segments(line: string, start: number): Segment[] {
  const out: Segment[] = [];
  let i = start;
  const n = line.length;
  while (i < n) {
    while (i < n && /\s/.test(line[i])) i++;
    if (i >= n) break;
    const segFrom = i;
    if (line[i] === "`") {
      let run = 0;
      while (i + run < n && line[i + run] === "`") run++;
      const fence = "`".repeat(run);
      const close = line.indexOf(fence, i + run);
      if (close !== -1) {
        i = close + run;
        out.push({ from: segFrom, to: i, text: line.slice(segFrom, i), atomic: "code" });
        continue;
      }
    }
    if (line.startsWith("[[", i)) {
      const close = line.indexOf("]]", i + 2);
      if (close !== -1) {
        i = close + 2;
        out.push({ from: segFrom, to: i, text: line.slice(segFrom, i), atomic: "link" });
        continue;
      }
    }
    while (i < n && !/\s/.test(line[i])) i++;
    out.push({ from: segFrom, to: i, text: line.slice(segFrom, i), atomic: null });
  }
  return out;
}

/** Returns true when `line` starts a GFM task list item. */
export function isTaskLine(line: string): boolean {
  return TASK_RE.test(line);
}

/** Parse one line. Returns null when the line is not a task line. */
export function parseTaskLine(line: string, dialect: Dialect = "default"): TaskLine | null {
  const m = TASK_RE.exec(line);
  if (!m) return null;
  const indent = m[1];
  const marker = m[2];
  const boxFrom = m[1].length + m[2].length + m[3].length;
  const boxTo = boxFrom + 3;
  // Need whitespace or EOL after the box.
  if (boxTo < line.length && !/\s/.test(line[boxTo])) return null;
  const checked = m[4] !== " ";

  const result: TaskLine = {
    indent,
    marker,
    checked,
    boxFrom,
    boxTo,
    contentFrom: boxTo,
    tokens: [],
    title: "",
    priority: null,
    priorityLetter: null,
    due: null,
    start: null,
    completion: null,
    projects: [],
    people: [],
    labels: [],
    status: null,
    recurrence: null,
    bucket: null,
    fields: {},
    links: [],
    anchor: null,
  };

  const segs = segments(line, boxTo);
  const titleParts: string[] = [];
  let first = true;
  let allowCompletion = checked;

  segs.forEach((seg, idx) => {
    const t = seg.text;
    const isLast = idx === segs.length - 1;
    const push = (kind: TokenKind, value: string, key?: string) => {
      const tok: Token = { kind, from: seg.from, to: seg.to, text: t, value };
      if (key !== undefined) tok.key = key;
      result.tokens.push(tok);
    };

    if (seg.atomic === "code") {
      push("code", t);
      titleParts.push(t);
      first = false;
      allowCompletion = false;
      return;
    }
    if (seg.atomic === "link") {
      const lm = LINK_RE.exec(t);
      const target = lm ? lm[1] : t.slice(2, -2);
      push("link", target);
      result.links.push(target);
      first = false;
      allowCompletion = false;
      return;
    }

    if (allowCompletion && DATE_RE.test(t)) {
      push("completion", t);
      result.completion = t;
      allowCompletion = false;
      return; // priority may still follow
    }
    allowCompletion = false;

    const pm = PRIORITY_RE.exec(t);
    if (first && pm) {
      push("priority", pm[1]);
      result.priorityLetter = pm[1];
      result.priority = priorityValue(pm[1]);
      first = false;
      return;
    }
    first = false;

    if (isLast && ANCHOR_RE.test(t)) {
      push("anchor", t.slice(1));
      result.anchor = t.slice(1);
      return;
    }
    if (t.length > 1 && PROJECT_RE.test(t)) {
      push("project", t.slice(1));
      result.projects.push(t.slice(1));
      return;
    }
    if (t.length > 1 && PERSON_RE.test(t)) {
      const handle = t.slice(1);
      if (dialect === "todotxt") {
        push("label", handle);
        result.labels.push(handle);
      } else {
        push("person", handle);
        result.people.push(handle);
      }
      return;
    }
    if (t.length > 1 && LABEL_RE.test(t)) {
      push("label", t.slice(1));
      result.labels.push(t.slice(1));
      return;
    }
    const fm = FIELD_RE.exec(t);
    if (fm && !URL_SCHEMES.has(fm[1]) && !fm[2].startsWith("//")) {
      const key = fm[1];
      const value = fm[2];
      if (key === "due") {
        push("due", value);
        result.due = value;
      } else if (key === "t" || key === "start") {
        push("start", value, key);
        result.start = value;
      } else if (key === "status") {
        push("status", value);
        result.status = value;
      } else if (key === "rec") {
        push("recurrence", value);
        result.recurrence = value;
      } else if (key === "bucket") {
        push("bucket", value);
        result.bucket = value;
      } else {
        push("field", value, key);
        result.fields[key] = value;
      }
      return;
    }
    push("word", t);
    titleParts.push(t);
  });

  result.title = titleParts.join(" ").replace(/\s+/g, " ").trim();
  return result;
}

// ---------------------------------------------------------------------------
// Dates

export interface ResolvedDate {
  date: string; // YYYY-MM-DD
  time: string | null; // HH:MM
}

const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function formatYmd(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function addDays(d: Date, days: number): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() + days);
  return r;
}

/**
 * Resolve a date token value (`2026-10-31`, `2026-10-31T14:00`, `today`, `tomorrow`,
 * `mon`..`sun` (next occurrence, today excluded), `+3d`, `+2w`, `+1m`) relative to `today`
 * (local calendar date). Returns null when not a valid date expression.
 */
export function resolveDate(value: string, today: Date = new Date()): ResolvedDate | null {
  const v = value.trim().toLowerCase();
  const abs = /^(\d{4})-(\d{2})-(\d{2})(?:t(\d{2}):(\d{2}))?$/.exec(v);
  if (abs) {
    const [, y, mo, d, hh, mm] = abs;
    const dt = new Date(Number(y), Number(mo) - 1, Number(d));
    if (dt.getFullYear() !== Number(y) || dt.getMonth() !== Number(mo) - 1 || dt.getDate() !== Number(d)) {
      return null;
    }
    if (hh !== undefined && (Number(hh) > 23 || Number(mm) > 59)) return null;
    return { date: `${y}-${mo}-${d}`, time: hh !== undefined ? `${hh}:${mm}` : null };
  }
  if (v === "today") return { date: formatYmd(today), time: null };
  if (v === "tomorrow") return { date: formatYmd(addDays(today, 1)), time: null };
  const wd = WEEKDAYS.indexOf(v.slice(0, 3));
  if (wd !== -1 && (v.length === 3 || WEEKDAYS_LONG[wd] === v)) {
    let diff = (wd - today.getDay() + 7) % 7;
    if (diff === 0) diff = 7;
    return { date: formatYmd(addDays(today, diff)), time: null };
  }
  const rel = /^\+(\d{1,4})([dwm])$/.exec(v);
  if (rel) {
    const n = Number(rel[1]);
    if (rel[2] === "d") return { date: formatYmd(addDays(today, n)), time: null };
    if (rel[2] === "w") return { date: formatYmd(addDays(today, n * 7)), time: null };
    const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const day = base.getDate();
    base.setDate(1);
    base.setMonth(base.getMonth() + n);
    const lastDay = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    base.setDate(Math.min(day, lastDay));
    return { date: formatYmd(base), time: null };
  }
  return null;
}

const WEEKDAYS_LONG = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

/** True when the resolved date (end of day if no time) is before `now`. */
export function isOverdue(value: string, now: Date = new Date()): boolean {
  const r = resolveDate(value, now);
  if (!r) return false;
  const [y, m, d] = r.date.split("-").map(Number);
  if (r.time) {
    const [hh, mm] = r.time.split(":").map(Number);
    return new Date(y, m - 1, d, hh, mm).getTime() < now.getTime();
  }
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime() < now.getTime();
}

// ---------------------------------------------------------------------------
// Document helpers

export interface DocTaskLine {
  lineNumber: number; // 0-based
  task: TaskLine;
}

/** All task lines in a markdown document, skipping fenced code blocks. */
export function taskLinesOf(markdown: string, dialect: Dialect = "default"): DocTaskLine[] {
  const out: DocTaskLine[] = [];
  let fence: string | null = null;
  markdown.split("\n").forEach((line, i) => {
    const fm = /^\s{0,3}(`{3,}|~{3,})/.exec(line);
    if (fm) {
      if (fence === null) fence = fm[1][0].repeat(fm[1].length);
      else if (fm[1][0] === fence[0] && fm[1].length >= fence.length) fence = null;
      return;
    }
    if (fence !== null) return;
    const task = parseTaskLine(line, dialect);
    if (task) out.push({ lineNumber: i, task });
  });
  return out;
}

/** Toggle the checkbox of a task line text. Returns null if not a task line. */
export function toggleTaskLine(line: string): string | null {
  const t = parseTaskLine(line);
  if (!t) return null;
  const box = t.checked ? "[ ]" : "[x]";
  return line.slice(0, t.boxFrom) + box + line.slice(t.boxTo);
}

/** Parse a quick-add input ("Fix bug @bob due:fri #ops") as if it were a task line. */
export function parseQuickAdd(text: string, dialect: Dialect = "default"): TaskLine {
  const parsed = parseTaskLine(`- [ ] ${text}`, dialect);
  // Always non-null: the prefix guarantees a task line.
  return parsed as TaskLine;
}
